import * as moment from "moment-timezone";
import * as React from "react";
import * as _ from "lodash";
import { toast } from "react-toastify";
import {
  useRouteMatch,
  useHistory
} from "react-router-dom";

import { ActionButton } from "src/library/components/Button";
import { IAction } from "src/library/components/Button/ActionButton";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import CancelListFromModal from "src/core/lists/components/CancelListModal";
import OrderDayPickerModal from "src/core/orders/components/Modals/OrderDayPickerModal";
import { ERPType } from "src/core/shared/api/globalSettings.api";
import useList from "src/core/lists/hooks/useList";
import { useDrop } from "src/core/drops/hooks";
import { IRole } from "src/core/shared/api/roles.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { useProductLeadTimeGroup } from "src/core/products/hooks/useProductLeadTimeGroup";

import { IList } from "../api/lists.api";

interface ListActionsListProps {
  list?: IList;
  role: IRole;
  terminology: ITerminology;
  erp: ERPType;
  reload?: () => void;
}

export const ListActionsList: React.ReactFCWithChildren<ListActionsListProps> = ({
  list, terminology, reload
}) => {
  const history = useHistory();
  const match = useRouteMatch<{ listId?: string }>();
  const listId = match && match.params && match.params.listId ? parseInt(match.params.listId, 0) : 0;
  const [ showDayPickerModal, setShowDayPickerModal ] = React.useState<boolean>(false);
  const [ createOrderFromListData, setCreateOrderFromListData ] = React.useState<number | null>(null);
  const checkUserPermission = useCheckUserPermission();
  const getProductLeadtimeGroup = useProductLeadTimeGroup();

  const [
    { currentList },
    {
      createOrderFromList, deleteList, setListMode
    }
  ] = useList(listId, list, false, reload);

  const [ deleteListModal, setCancelListModal ] = React.useState<boolean>(false);
  let actionButtonItems: IAction[] = [];
  const [ { drop } ] = useDrop();

  /**
   * Creates an order from the list
   */
  const createOrderFromListButton = React.useCallback(() => {
    if (checkUserPermission("create-order")) {
      if (currentList.data && drop && drop.id) {
        const list = currentList.data;

        return [
          {
            title: `Create ${terminology.order.singular.text} from ${terminology.list.singular.text}`,
            action: () => {
              setCreateOrderFromListData(list.id);
              setShowDayPickerModal(true);
            }
          }
        ];
      }
    }

    return [];
  }, [
    checkUserPermission,
    currentList.data,
    drop,
    terminology.order.singular.text,
    terminology.list.singular.text
  ]);

  /**
   * View list button
   */
  const viewListButton = React.useCallback(() => {
    if (!match.params.listId && currentList.data) {
      return [
        {
          title: `View ${terminology.list.singular.text}`,
          action: () => {
            history.push(`/lists/${(currentList.data as IList).id}`);
          }
        }
      ];
    }

    return [];
  }, [
    currentList.data,
    history,
    match.params.listId,
    terminology.list.singular.text
  ]);

  /**
   * view list orders button
   */
  const viewListOrdersButton = React.useCallback(() => {
    if (currentList.data) {
      return [
        {
          title: `View related ${terminology.order.plural.text}`,
          action: () => {
            history.push(`/lists/${(currentList.data as IList).id}/orders`, { ordersViewType: "table" });
          }
        }
      ];
    }

    return [];
  }, [
    currentList.data,
    history,
    terminology.order.plural.text
  ]);

  const validateDelete = React.useCallback(() => {
    // only allow deletion request if the schedule is suspended or the end of the schedule is in the past.
    if (!currentList?.data?.schedule || currentList?.data?.schedule?.status === "suspended" ||
      (currentList?.data?.schedule?.endDate && moment.utc(currentList.data.schedule.endDate).isBefore(moment.utc().toDate()))) {
      setCancelListModal(true);
    } else {
      toast.error("Standing orders must be cancelled or have an end date in the past before they can be deleted.");
    }
  }, [ currentList.data?.schedule ]);

  /**
   * delete list button (only available without a schedule)
   */
  const deleteListButton = React.useCallback(() => {
    if (checkUserPermission("delete-list")) {
      return [
        {
          title: `Delete ${terminology.list.singular.text}`,
          action: () => validateDelete()
        }
      ];
    }

    return [];
  }, [
    checkUserPermission,
    terminology.list.singular.text,
    validateDelete
  ]);

  /**
   * Amend order button
   */
  const amendListButton = React.useCallback(() => {
    if (checkUserPermission("amend-list")) {
      if (currentList.data) {
        return [
          {
            action: () => {
              if (match.params && match.params.listId) {
                setListMode("edit");
              } else {
                history.push(`/lists/${(currentList.data as IList).id}`, { edit: true });
              }
            },
            title: `Edit ${terminology.list.singular.text}`
          }
        ];
      }
    }

    return [];
  }, [
    checkUserPermission,
    currentList.data,
    history,
    match.params,
    setListMode,
    terminology.list.singular.text
  ]);

  // Set the action button items array
  actionButtonItems = [
    ...viewListButton(),
    ...amendListButton(),
    ...viewListOrdersButton(),
    ...createOrderFromListButton(),
    ...deleteListButton()
  ];

  const minimumDate = React.useMemo(() => {
    const longestLeadTimes = _.max(_.map(currentList.data?.listLines, ol => {
      const productLeadtimeGroup = getProductLeadtimeGroup(ol.product);

      if (productLeadtimeGroup) {
        return productLeadtimeGroup.defaultLeadTimeDays;
      }

      return 0;
    })) || 0;

    return moment.utc().add(longestLeadTimes, "days").toDate();
  }, [ currentList.data?.listLines, getProductLeadtimeGroup ]);

  return (
    <>
      <ActionButton
        title={`${terminology.list.singular.text} actions`}
        items={actionButtonItems}
        disabled={actionButtonItems.length === 0}
      />

      {currentList.data && (
        <CancelListFromModal
          list={currentList.data}
          show={deleteListModal}
          handleDismiss={() => setCancelListModal(false)}
          onConfirm={() => {
            if (currentList.data) {
              deleteList(currentList.data.id);
            }
          }}
          terminology={terminology}
        />
      )}

      {currentList && drop && (
        <OrderDayPickerModal
          drop={drop}
          terminology={terminology}
          show={showDayPickerModal}
          fromDate={minimumDate}
          handleDismiss={() => setShowDayPickerModal(false)}
          modalTitle="Create Order From List"
          onConfirm={(date: Date, editBeforePlacing: boolean, customerReference?: string) => createOrderFromListData && createOrderFromList({
            id: createOrderFromListData,
            deliveryDate: date,
            customerReference,
            editBeforePlacing
          })}
        />
      )}
    </>
  );
};
